import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce, omit } from 'lodash';
import { useGetPerformanceInvestment } from 'services/calculations/financialPerformance/hooks';
import { IPerformanceInvestmentPeriodsObject } from 'services/calculations/financialPerformance/types';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { PROJECTED_CASH_FLOW_OPTION_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

const hasAllPerformanceInvestmentFields = (requestData: Record<string, unknown>) => {
  return Object.values(requestData).every((value) => value !== undefined && value !== null);
};

export const usePerformanceInvestment = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getValuesInPercents = (object: IPerformanceInvestmentPeriodsObject) => {
    return {
      y1: object.y1 * 100,
      y3: object.y3 * 100,
      y5: object.y5 * 100,
    };
  };

  const getPerformanceInvestment = useGetPerformanceInvestment();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [
    returnOfInvestmentField,
    returnOfEquityField,
    returnOfAssetsField,
    rateOfReturnField,
    netPresentValueField,
    payBackPeriodField,
  ] = observedFields.performanceInvestment.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (requestData, unit) => {
        if (!hasAllPerformanceInvestmentFields(requestData)) return;

        const params = {
          unit,
          businessPlanId,
          requestData,
        };

        try {
          const { returnOfInvestment, returnOfEquity, returnOfAsset, npv, irr, payBackPeriod } =
            await getPerformanceInvestment.mutateAsync(params);

          setValue(returnOfInvestmentField, getValuesInPercents(returnOfInvestment));
          setValue(returnOfEquityField, getValuesInPercents(returnOfEquity));
          setValue(returnOfAssetsField, getValuesInPercents(returnOfAsset));
          setValue(rateOfReturnField, irr);
          setValue(netPresentValueField, npv);
          setValue(payBackPeriodField, payBackPeriod);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const performanceInvestmentHandler = useCallback(
    async (content: IFormContentTypes['performanceInvestment']) => {
      if (!canAccess) return;
      const [
        stockholderEquity,
        totalAsset,
        discountRateStr,
        netProfitData,
        purchaseOfPropertyPlantAndEquipmentData,
        investingActivitiesTable,
        netCashProvidedByOperatingActivities,
        sourcesOfProjectFinancing,
        netCashProvidedByIndirectOperatingActivities,
        selectedType,
        operationUnit,
      ] = content;

      const firstYearStockholderEquity = Number(stockholderEquity.yearsValues.y1);
      const thirdYearStockholderEquity = Number(stockholderEquity.yearsValues.y3);
      const fifthYearStockholderEquity = Number(stockholderEquity.yearsValues.y5);
      const firstYearTotalAsset = Number(totalAsset.yearsValues.y1);
      const thirdYearTotalAsset = Number(totalAsset.yearsValues.y3);
      const fifthYearTotalAsset = Number(totalAsset.yearsValues.y5);
      const discountRate = Number(discountRateStr);
      const purchaseOfPropertyPlantAndEquipment = purchaseOfPropertyPlantAndEquipmentData;
      const purchaseOfIntangibleAndOtherNonCurrentAssets = investingActivitiesTable[0];
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;
      const receiptOfGovernmentGrants = omit(investingActivitiesTable[4], 'id');

      const performanceInvestmentRequestData = {
        firstYearStockholderEquity,
        thirdYearStockholderEquity,
        fifthYearStockholderEquity,
        firstYearTotalAsset,
        thirdYearTotalAsset,
        fifthYearTotalAsset,
        discountRate,
        netProfitData,
        purchaseOfPropertyPlantAndEquipment,
        purchaseOfIntangibleAndOtherNonCurrentAssets,
        netCashProvidedByOperatingActivities:
          selectedType === PROJECTED_CASH_FLOW_OPTION_NAMES.DIRECT
            ? netCashProvidedByOperatingActivities
            : netCashProvidedByIndirectOperatingActivities,
        receiptOfGovernmentGrants,
        sourcesOfProjectFinancing,
      };

      debouncedUpdateFunction(performanceInvestmentRequestData, unit);
    },

    [debouncedUpdateFunction],
  );

  return performanceInvestmentHandler;
};
